import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivateFn } from "@angular/router";
import { AuthService } from "./services/auth.service";
import { checkGroupsInStorage, getGroupsFromStorage } from "./storage-utils";

/**
 * Group-membership check for a route. Runs alongside MsalGuard, which owns signing in
 * and processing the login redirect; this only inspects the account that results.
 *
 * It used to extend a hand-copied fork of MsalGuard and process the redirect itself,
 * making it a third redirect handler next to MsalRedirectComponent and the real guard,
 * and cutting the app off from every fix the library ships to its own guard.
 */
export const groupGuard: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
    const auth = inject(AuthService);
    const requiredGroups: string[] = route.data['requiredGroups'] ?? [];

    // Settles once MSAL has finished any redirect and the account is known.
    await auth.whenReady();

    const account = auth.activeAccount();

    // No account means nobody is signed in yet. MsalGuard runs alongside this guard and
    // is already sending the browser to Microsoft; judging group membership here would
    // only put an alert in front of that redirect. Deny quietly and let it proceed.
    if (!account)
        return false;

    const claims = account.idTokenClaims;

    if (!claims?.groups && !checkGroupsInStorage(account)) {
        if (claims?._claim_names?.groups) {
            // Group overage: the token carries a pointer instead of the list. The
            // Graph fallback this was scaffolded for was never built (there is no
            // /overage route), so deny rather than navigate somewhere that does not exist.
            window.alert('Your account has too many group memberships for them to be included in the token. Please contact an administrator.');
            return false;
        }

        window.alert('Token does not have groups claim. Please ensure that your account is assigned to a security group and then sign-out and sign-in again.');
        return false;
    }

    const memberOf = claims?.groups ?? getGroupsFromStorage(account) ?? [];
    const allowed = requiredGroups.some(group => memberOf.includes(group));

    if (!allowed)
        window.alert('You do not have access. Please ensure that your account is assigned to the required security group and then sign-out and sign-in again.');

    return allowed;
};
